export interface SessionUser {
  id: string;
  email: string;
  displayName: string;
  role: 'admin' | 'employee';
  mustChangePassword: boolean;
  isTest: boolean;
}

export interface DayClosure {
  id: string;
  close_type: 'partial' | 'final';
  cash_cents: number;
  tpv1_cents: number;
  tpv2_cents: number;
  opening_150: number | null;
  leaves_150: number | null;
  missing_partial: number;
  created_at: string;
  display_name: string;
}

export interface ClosureDetail extends DayClosure {
  business_date: string;
  employee_email: string;
  employee_name: string;
  is_test: number;
  payments: Array<{
    id: string;
    concept: string;
    amount_cents: number;
    authorized_by: string;
    attachment_id: string;
    original_filename: string;
  }>;
  corrections: Array<{
    id: string;
    reason: string;
    replacement_json: string;
    created_at: string;
    admin_name: string;
  }>;
  notifications: Array<{
    id: string;
    recipient: string;
    status: 'pending' | 'queued' | 'sending' | 'sent' | 'delivered' | 'bounced' | 'failed';
    attempts: number;
    last_error: string | null;
    updated_at: string;
    sent_at: string | null;
    delivered_at: string | null;
  }>;
}

export interface AdminUser {
  id: string;
  email: string;
  display_name: string;
  role: 'admin' | 'employee';
  must_change_password: number;
  active: number;
  is_test: number;
  created_at: string;
}

export class ApiError extends Error {
  constructor(message: string, readonly code: string, readonly status: number) {
    super(message);
  }
}

async function parse<T>(response: Response): Promise<T> {
  const body = await response.json().catch(() => ({ error: 'Respuesta no válida del servidor.' })) as { error?: string; code?: string } & T;
  if (!response.ok) throw new ApiError(body.error ?? 'No se ha podido completar la operación.', body.code ?? 'UNKNOWN', response.status);
  return body;
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  return parse<T>(await fetch(path, { ...init, headers, credentials: 'same-origin' }));
}

export const money = (cents: number) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(cents / 100);

export function eurosToCents(value: string): number | null {
  const normalized = value.trim().replace(',', '.');
  if (!/^\d+(\.\d{0,2})?$/.test(normalized)) return null;
  const amount = Number(normalized);
  if (!Number.isFinite(amount) || amount < 0) return null;
  return Math.round(amount * 100);
}

export function centsToEuros(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}
